import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    DocumentTextIcon,
    EnvelopeIcon,
    RocketLaunchIcon,
    ArrowDownTrayIcon,
    CalendarIcon,
    CreditCardIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import useApiWithAuth from '../utils/useApiWithAuth';
import { Helmet } from 'react-helmet';
import { useToast } from '../components/ToastProvider';

const MyPremiumServices = () => {
    const [purchases, setPurchases] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { apiCall } = useApiWithAuth();
    const token = useSelector((state) => state.auth.token);
    const toast = useToast();

    const serviceNames = {
        'cv-reformatted': 'CV Reformé Professionnel',
        'cover-letter': 'Lettre de Motivation IA',
        'complete-pack': 'Pack Complet'
    };

    const serviceIcons = {
        'cv-reformatted': DocumentTextIcon,
        'cover-letter': EnvelopeIcon,
        'complete-pack': RocketLaunchIcon
    };

    const paymentMethodLabels = {
        'stripe': 'Carte bancaire',
        'credits': 'Crédits'
    };

    useEffect(() => {
        fetchPurchases();
    }, []);

    const fetchPurchases = async () => {
        try {
            setIsLoading(true);
            const response = await apiCall('https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/my-purchases');
            const data = await response.json();
            setPurchases(data.purchases || []);
        } catch (error) {
            console.error('Erreur lors de la récupération des achats:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadService = async (serviceType, analysisId, isCompletePack = false) => {
        try {
            if (isCompletePack) {
                // Pour le pack complet, télécharger les deux fichiers
                const cvResponse = await apiCall(
                    `https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/download/cv-reformatted/${analysisId}`
                );
                const cvBlob = await cvResponse.blob();
                const cvUrl = window.URL.createObjectURL(cvBlob);
                const cvLink = document.createElement('a');
                cvLink.href = cvUrl;
                cvLink.download = `CV_Reforme_${Date.now()}.docx`;
                document.body.appendChild(cvLink);
                cvLink.click();
                document.body.removeChild(cvLink);
                window.URL.revokeObjectURL(cvUrl);

                // Attendre un peu avant de télécharger la lettre
                setTimeout(async () => {
                    const letterResponse = await apiCall(
                        `https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/download/cover-letter/${analysisId}`
                    );
                    const letterBlob = await letterResponse.blob();
                    const letterUrl = window.URL.createObjectURL(letterBlob);
                    const letterLink = document.createElement('a');
                    letterLink.href = letterUrl;
                    letterLink.download = `Lettre_Motivation_${Date.now()}.docx`;
                    document.body.appendChild(letterLink);
                    letterLink.click();
                    document.body.removeChild(letterLink);
                    window.URL.revokeObjectURL(letterUrl);
                }, 500);
            } else {
                const response = await apiCall(
                    `https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/download/${serviceType}/${analysisId}`
                );
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${serviceNames[serviceType]}_${Date.now()}.docx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            toast.error(
                'Téléchargement',
                'Impossible de récupérer le fichier. Vérifiez votre connexion et réessayez.'
            );
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="page-shell p-4">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell p-4">
            <Helmet>
                <title>Mes services — CV Analyse</title>
            </Helmet>

            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                            <SparklesIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-800">Mes Services Premium</h1>
                    </div>
                    <p className="text-gray-600">Retéléchargez vos services premium achetés</p>
                </div>

                {purchases.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <DocumentTextIcon className="w-10 h-10 text-gray-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Aucun service premium acheté</h2>
                        <p className="text-gray-600 mb-6">
                            Vous n'avez pas encore acheté de service premium.
                        </p>
                        <a
                            href="/upload"
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all"
                        >
                            Analyser mon CV
                        </a>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {purchases.map((purchase) => {
                            const Icon = serviceIcons[purchase.serviceType] || DocumentTextIcon;
                            const isCompletePack = purchase.serviceType === 'complete-pack';

                            return (
                                <div
                                    key={purchase.id}
                                    className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex items-start gap-4 flex-1">
                                            <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Icon className="w-8 h-8 text-blue-600" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-xl font-bold text-gray-800 mb-1">
                                                    {serviceNames[purchase.serviceType]}
                                                </h3>
                                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <CalendarIcon className="w-4 h-4" />
                                                        {formatDate(purchase.createdAt)}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <CreditCardIcon className="w-4 h-4" />
                                                        {paymentMethodLabels[purchase.paymentMethod]}
                                                        {purchase.paymentMethod === 'credits' && purchase.creditsUsed && (
                                                            <span className="ml-1">({purchase.creditsUsed} crédits)</span>
                                                        )}
                                                    </div>
                                                    {purchase.analysis && (
                                                        <div className="text-gray-500">
                                                            Score CV: {purchase.analysis.score}/100
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-3">
                                            {isCompletePack ? (
                                                <>
                                                    <button
                                                        onClick={() => downloadService('cv-reformatted', purchase.analysisId, false)}
                                                        className="flex items-center gap-2 bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-blue-700 transition-all"
                                                    >
                                                        <DocumentTextIcon className="w-5 h-5" />
                                                        CV
                                                    </button>
                                                    <button
                                                        onClick={() => downloadService('cover-letter', purchase.analysisId, false)}
                                                        className="flex items-center gap-2 bg-purple-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-purple-700 transition-all"
                                                    >
                                                        <EnvelopeIcon className="w-5 h-5" />
                                                        Lettre
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => downloadService(purchase.serviceType, purchase.analysisId, false)}
                                                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all"
                                                >
                                                    <ArrowDownTrayIcon className="w-5 h-5" />
                                                    Télécharger
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyPremiumServices;

